import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import * as React from "react";

import { listAssignableBdms, type AssignableBdm } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const optionId = (id: string) => `wiz-assignee-opt-${id}`;

interface Props {
  accessToken: string;
  /** Selected BDM/PO id, or null when nothing is chosen yet. */
  value: string | null;
  /** Receives the chosen id and the candidate's display name (person name, or
   * email fallback) — the name feeds the review step's owner-identity block
   * (CHP-809). Both null on clear. */
  onChange: (id: string | null, name: string | null) => void;
}

const NOT_CERTIFIED_HINT =
  "Not certified — must complete compulsory training before being assigned deals";

/** The candidate's person name (email fallback) — what the review step shows
 * as the deal owner. Excludes the company (that's the partner-org row). */
function candidateName(b: AssignableBdm): string {
  return b.full_name ?? b.email;
}

function candidateLabel(b: AssignableBdm): string {
  const name = candidateName(b);
  return b.company_name ? `${name} — ${b.company_name}` : name;
}

/**
 * CHP-202/CHP-325: the on-behalf assignee picker shown at the top of the
 * registration wizard's first step for CM/RA/RM. Lists ALL active BDMs/POs
 * (from GET /deals/assignable-bdms), searchable by person OR company name (and
 * email). The chosen id is wired into the draft's `target_bdm_id`.
 *
 * CHP-325 follow-up (review feedback): rendered as an idiomatic combobox — the
 * candidate list surfaces in a popup on focus rather than a permanent inline
 * block. Non-certified candidates (RACCPE-22/23 lockout) carry a warning icon
 * (hover for the reason) and are non-selectable; the reason is also exposed to
 * screen readers via sr-only text.
 */
export function DealAssigneeSelector({ accessToken, value, onChange }: Props) {
  const [search, setSearch] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useQuery<AssignableBdm[]>({
    queryKey: ["deals", "assignable-bdms", accessToken],
    queryFn: () => listAssignableBdms(accessToken),
    enabled: !!accessToken,
  });

  const candidates = React.useMemo(() => data ?? [], [data]);
  const selected = candidates.find((b) => b.id === value) ?? null;

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((b) =>
      [b.full_name, b.email, b.company_name].some((f) => f?.toLowerCase().includes(q)),
    );
  }, [candidates, search]);

  // Close the popup on an outside click (mirrors DealAdvancedFilters).
  React.useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  // CHP-805: combobox keyboard contract — roving aria-activedescendant over the
  // candidate options (Arrow Up/Down move, Enter selects a certified one,
  // Escape closes), so the listbox is keyboard-navigable and the options are
  // plain role="option" elements rather than focusable buttons.
  const [activeIndex, setActiveIndex] = React.useState(-1);
  React.useEffect(() => setActiveIndex(-1), [search, open]);
  React.useEffect(() => {
    if (!open || activeIndex < 0) return;
    const b = filtered[activeIndex];
    if (b) document.getElementById(optionId(b.id))?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open, filtered]);

  function select(b: AssignableBdm) {
    onChange(b.id, candidateName(b));
    setOpen(false);
    setSearch("");
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (filtered.length > 0) setActiveIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (filtered.length > 0) setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Enter" && open && activeIndex >= 0) {
      const b = filtered[activeIndex];
      // Only certified candidates are selectable (RACCPE-22/23 lockout).
      if (b && b.certified) {
        e.preventDefault();
        select(b);
      }
    }
  }

  return (
    <div className="grid gap-2">
      <Label htmlFor="wiz-assignee-search">Assign this deal to *</Label>
      <p className="text-xs text-muted-foreground">
        You will only be able to assign certified BDMs and Program Owners to a deal registration.
      </p>

      {selected ? (
        <div className="flex items-center justify-between rounded-md border border-input bg-background p-2">
          <span className="text-sm" data-testid="assignee-selected">
            {candidateLabel(selected)}
          </span>
          <Button type="button" variant="outline" size="sm" onClick={() => onChange(null, null)}>
            Change
          </Button>
        </div>
      ) : (
        <div className="relative" ref={containerRef}>
          <Input
            id="wiz-assignee-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder="Search by name, email, or company"
            role="combobox"
            aria-expanded={open}
            aria-controls="wiz-assignee-listbox"
            aria-autocomplete="list"
            aria-activedescendant={
              open && activeIndex >= 0 && filtered[activeIndex]
                ? optionId(filtered[activeIndex]!.id)
                : undefined
            }
            autoComplete="off"
          />
          {open &&
            (isLoading ? (
              <p
                className="absolute z-20 mt-1 w-full rounded-md border border-input bg-background p-2 text-sm text-muted-foreground shadow-overlay"
                role="status"
              >
                Loading partners…
              </p>
            ) : error ? (
              <p
                className="absolute z-20 mt-1 w-full rounded-md border border-input bg-background p-2 text-sm text-destructive shadow-overlay"
                role="alert"
              >
                Couldn't load partners. Refresh and try again.
              </p>
            ) : (
              <ul
                id="wiz-assignee-listbox"
                className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-input bg-background shadow-overlay"
                role="listbox"
                aria-label="Assignable partners"
              >
                {filtered.length === 0 ? (
                  <li className="p-2 text-sm text-muted-foreground">No partners match.</li>
                ) : (
                  filtered.map((b, i) =>
                    b.certified ? (
                      <li
                        key={b.id}
                        id={optionId(b.id)}
                        role="option"
                        aria-selected={i === activeIndex}
                        onClick={() => select(b)}
                        className={cn(
                          "block w-full cursor-pointer px-2 py-1.5 text-left text-sm hover:bg-muted",
                          i === activeIndex && "bg-muted",
                        )}
                        data-testid={`assignee-option-${b.id}`}
                      >
                        {candidateLabel(b)}{" "}
                        <span className="text-muted-foreground">({b.email})</span>
                      </li>
                    ) : (
                      <li
                        key={b.id}
                        id={optionId(b.id)}
                        role="option"
                        aria-selected={false}
                        aria-disabled="true"
                        title={NOT_CERTIFIED_HINT}
                        className={cn(
                          "flex w-full cursor-not-allowed items-center gap-1.5 px-2 py-1.5 text-left text-sm text-muted-foreground",
                          i === activeIndex && "bg-muted",
                        )}
                        data-testid={`assignee-option-${b.id}`}
                        data-disabled="true"
                      >
                        <AlertTriangle
                          className="h-3.5 w-3.5 shrink-0 text-amber-500"
                          aria-hidden="true"
                        />
                        <span>
                          {candidateLabel(b)} <span>({b.email})</span>
                        </span>
                        <span className="sr-only">(not certified)</span>
                      </li>
                    ),
                  )
                )}
              </ul>
            ))}
        </div>
      )}
    </div>
  );
}
